import { Alert, Text, VFlow } from 'bold-ui'
import { FieldArray } from 'components/form'
import { useFlags } from 'config/useFlagsContext'
import { useCategoriasMedicamentosQuery, useTiposReceitaProibidosQuery } from 'graphql/hooks.generated'
import { ListaMedicamentoEnum } from 'graphql/types.generated'
import { useFirebase } from 'hooks/firebase/useFirebase'
import { useForceRender } from 'hooks/useForceRender'
import { Dispatch, SetStateAction, useMemo, useRef } from 'react'
import { emptyArrayOf } from 'util/array'
import { metaPath } from 'util/metaPath'

import { EmailUpdateForm } from '../../../components/EmailUpdateForm'
import { MedicamentoFormModel } from '../../model-prescricao'
import { PrescricaoMedicamentoPlanoModel } from '../../PrescricaoMedicamentoView'
import { filtrarMedicamentos, resolveCategoria, resolveTipoReceita } from '../../utils/converter-prescricao'
import { EncaminharPrescricaoMedicamentoItem } from '../components/EncaminharPrescricaoMedicamentoItem'
import {
  MedicamentoPrescricaoDigitalSelected,
  PrescricaoDigitalFormModel,
  PrescricaoDigitalIntegracaoModel,
} from './model-prescricaoDigital'
import { PrescricaoDigitalIntegracaoMessage } from './PrescricaoDigitalIntegracaoMessage'
import { PrescricaoDigitalMedicamentosNaoPermitidosList } from './PrescricaoDigitalMedicamentosNaoPermitidosList'

const path = metaPath<PrescricaoMedicamentoPlanoModel>()

interface GerarPrescricaoDigitalFieldArrayProps {
  cidadaoId: ID
  cidadaoEmail: string
  prescricaoDigital: PrescricaoDigitalFormModel
  medicamentos: MedicamentoFormModel[]
  medicamentosSelected: MedicamentoFormModel[]
  setMedicamentosSelected: (medicamentos: MedicamentoFormModel[]) => void
  medicamentoSelected: MedicamentoPrescricaoDigitalSelected
  onChangeMedicamento: Dispatch<SetStateAction<MedicamentoPrescricaoDigitalSelected>>
  statusMessage: PrescricaoDigitalIntegracaoModel
  setStatusMessage: (statusMessage: PrescricaoDigitalIntegracaoModel) => void
  setIsEditingEmail: Dispatch<SetStateAction<boolean>>
  isEditingEmail: boolean
}

export function GerarPrescricaoDigitalFieldArray(props: GerarPrescricaoDigitalFieldArrayProps) {
  const {
    cidadaoId,
    cidadaoEmail,
    prescricaoDigital,
    medicamentos,
    medicamentosSelected,
    setMedicamentosSelected,
    medicamentoSelected,
    onChangeMedicamento,
    statusMessage,
    setStatusMessage,
    setIsEditingEmail,
    isEditingEmail,
  } = props
  const { analytics } = useFirebase()
  const alertUpdateEmailSucceedRef = useRef(false)
  const forceRender = useForceRender()
  const { PILOTO_PRESCRICAO_CONTROLADA_ENABLED } = useFlags()
  const {
    data: { tiposReceitaProibidos },
  } = useTiposReceitaProibidosQuery({ fetchPolicy: 'cache-first' })
  const {
    data: { categoriasMedicamentos },
  } = useCategoriasMedicamentosQuery({ fetchPolicy: 'cache-first' })

  const categoriasProibidas = useMemo(
    () => categoriasMedicamentos?.filter((item) => !item.disponivelParaPrescricaoDigital).map((item) => item.nome),
    [categoriasMedicamentos]
  )

  const categoriasCompativeisByCategoriaMedicamento = useMemo(
    () =>
      new Map<string, Readonly<ListaMedicamentoEnum[]>>(
        categoriasMedicamentos
          ? categoriasMedicamentos.map((categoriaMedicamento) => [
              categoriaMedicamento.nome,
              categoriaMedicamento.categoriasCompativeis,
            ])
          : emptyArrayOf()
      ),
    [categoriasMedicamentos]
  )

  const { medicamentosPermitidos, medicamentosNaoPermitidos } = filtrarMedicamentos(
    medicamentos,
    categoriasProibidas,
    tiposReceitaProibidos
  )

  const handleEmailChange = (newEmail: string) => {
    analytics.logEvent('click_atualizar_email_prescricao_digital')
    if (newEmail !== cidadaoEmail) {
      alertUpdateEmailSucceedRef.current = true
    }
  }

  const handleAlertEmailClose = () => {
    alertUpdateEmailSucceedRef.current = false
    forceRender()
  }

  const handleAlertStatusClose = () => {
    setStatusMessage(null)
  }

  const handleOnEditarEmailClick = () => {
    analytics.logEvent('click_editar_email_prescricao_digital')
  }

  const handleOnChangeMedicamentosSelected = (checked: boolean, medicamento: MedicamentoFormModel) => {
    if (checked) {
      const tipoReceita = resolveTipoReceita(medicamento)
      const categoria = resolveCategoria(medicamento)
      onChangeMedicamento((prevMedicamento) => ({
        tipoReceita: tipoReceita,
        categoriasCompativeis:
          categoriasCompativeisByCategoriaMedicamento.get(categoria) ?? prevMedicamento?.categoriasCompativeis,
      }))
      setMedicamentosSelected([...medicamentosSelected, medicamento])
    } else {
      const medicamentosSelecionadosRestantes = medicamentosSelected.filter((item) => item.id !== medicamento.id)
      setMedicamentosSelected(medicamentosSelecionadosRestantes)

      if (medicamentosSelecionadosRestantes.isEmpty()) {
        onChangeMedicamento(null)
      } else {
        const restoNaoManual = medicamentosSelecionadosRestantes.filter((medicamento) => !medicamento.registroManual)
        if (restoNaoManual.isEmpty()) {
          // medicamentos manuais sao compativeis com qualquer categoria
          onChangeMedicamento((prevMedicamento) => ({
            ...prevMedicamento,
            categoriasCompativeis: null,
          }))
        }
      }
    }
  }

  return (
    <FieldArray
      name={path.medicamentos.absolutePath()}
      render={() => (
        <VFlow vSpacing={0.5}>
          {!!statusMessage && (
            <PrescricaoDigitalIntegracaoMessage {...statusMessage} onAlertClose={handleAlertStatusClose} />
          )}
          {!!alertUpdateEmailSucceedRef.current && (
            <Alert inline type='success' onCloseClick={handleAlertEmailClose}>
              E-mail atualizado com sucesso.
            </Alert>
          )}
          <Alert inline type='info'>
            A prescrição será assinada eletronicamente e será enviada ao cidadão através do e-mail cadastrado. A
            responsabilidade dos dados enviados a este e-mail é do prescritor. Verifique atentamente os dados da
            prescrição antes de assinar e enviar.
          </Alert>
          {PILOTO_PRESCRICAO_CONTROLADA_ENABLED && (
            <Text>
              Selecione os medicamentos que deseja incluir na prescrição digital.{' '}
              <Text fontWeight='bold'>Só podem ser incluídos medicamentos com o mesmo tipo de receita.</Text>
            </Text>
          )}
          <EmailUpdateForm
            cidadaoId={cidadaoId}
            cidadaoEmail={cidadaoEmail}
            onSubmitSuccess={handleEmailChange}
            editing={isEditingEmail}
            onEditingChange={setIsEditingEmail}
            onEditarClick={handleOnEditarEmailClick}
            initialValues={{ email: cidadaoEmail }}
          />
          <EncaminharPrescricaoMedicamentoItem
            prescricaoDigital={prescricaoDigital}
            medicamentosToPrint={PILOTO_PRESCRICAO_CONTROLADA_ENABLED ? medicamentosPermitidos : medicamentos}
            medicamentoIdsSelecionado={medicamentosSelected.map((item) => item.id)}
            onChange={handleOnChangeMedicamentosSelected}
            disableReceitaControlada
            isPrescricaoDigital
            medicamentoSelected={medicamentoSelected}
          />
          {PILOTO_PRESCRICAO_CONTROLADA_ENABLED && medicamentosNaoPermitidos?.isNotEmpty() && (
            <PrescricaoDigitalMedicamentosNaoPermitidosList medicamentosNaoPermitidos={medicamentosNaoPermitidos} />
          )}
        </VFlow>
      )}
    />
  )
}
